class KlaviyoBis extends HTMLElement {
  #triggerButton = null;
  #clickHandler;
  #variantUpdateHandler;
  #variantChangeHandler;

  get #dialog() { return this.querySelector('.klaviyo-bis-dialog'); }
  get #form() { return this.querySelector('.klaviyo-bis-form'); }
  get #emailInput() { return this.querySelector('.klaviyo-bis-email'); }
  get #errorEl() { return this.querySelector('.klaviyo-bis-error'); }
  get #successEl() { return this.querySelector('.klaviyo-bis-success'); }
  get #formContent() { return this.querySelector('.klaviyo-bis-form-content'); }
  get #submitBtn() { return this.querySelector('.klaviyo-bis-submit'); }

  connectedCallback() {
    this.#bindEvents();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#clickHandler);
    document.removeEventListener('variant:update', this.#variantUpdateHandler);
    document.removeEventListener('variant:change', this.#variantChangeHandler);
  }

  #bindEvents() {
    this.querySelector('.klaviyo-bis-close')?.addEventListener('click', () => this.#closeWithAnimation());

    this.#dialog?.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.#closeWithAnimation();
    });

    this.#dialog?.addEventListener('click', (e) => {
      if (e.target === this.#dialog) this.#closeWithAnimation();
    });

    this.#form?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.#handleSubmit();
    });

    this.#clickHandler = (e) => {
      const trigger = e.target.closest('[data-bis-trigger]');
      if (!trigger) return;
      const hostId = trigger.dataset.bisHostId;
      if (hostId && hostId !== this.id) return;
      e.preventDefault();
      this.#triggerButton = trigger;
      this.#openModal(trigger);
    };
    document.addEventListener('click', this.#clickHandler);

    this.#variantUpdateHandler = (e) => {
      const variant = e.detail?.resource;
      if (!variant) return;
      this.#syncVariant(variant.id, variant.available === false);
    };
    document.addEventListener('variant:update', this.#variantUpdateHandler);

    this.#variantChangeHandler = (e) => {
      const variant = e.detail?.variant;
      if (!variant) return;
      this.#syncVariant(variant.id, !variant.available);
    };
    document.addEventListener('variant:change', this.#variantChangeHandler);
  }

  #syncVariant(variantId, isSoldOut) {
    if (!this.id) return;
    document.querySelectorAll(`[data-bis-host-id="${this.id}"][data-bis-trigger]`).forEach((btn) => {
      btn.dataset.variantId = variantId;
    });
    if (this.getAttribute('sold-out-only') !== 'true') return;
    document.querySelectorAll(`[data-bis-host-id="${this.id}"][data-bis-sold-out-only]`).forEach((wrap) => {
      wrap.hidden = !isSoldOut;
    });
  }

  #openModal(trigger) {
    const dialog = this.#dialog;
    if (!dialog) return;
    dialog.dataset.variantId = trigger.dataset.variantId || this.getAttribute('variant-id') || '';
    dialog.dataset.listId = trigger.dataset.listId || this.getAttribute('list-id') || '';
    this.#resetForm();
    dialog.showModal();
  }

  #closeWithAnimation() {
    const dialog = this.#dialog;
    if (!dialog || dialog.classList.contains('klaviyo-bis-closing')) return;
    dialog.classList.add('klaviyo-bis-closing');

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#finishClose(dialog);
      return;
    }

    this.querySelector('.klaviyo-bis-wrap')?.addEventListener(
      'animationend',
      () => this.#finishClose(dialog),
      { once: true }
    );
  }

  #finishClose(dialog) {
    dialog.classList.remove('klaviyo-bis-closing');
    dialog.close();
    this.#triggerButton?.focus();
    this.#triggerButton = null;
  }

  #resetForm() {
    this.#form?.reset();
    this.#hideError();
    this.#emailInput?.removeAttribute('aria-invalid');
    if (this.#successEl) this.#successEl.hidden = true;
    if (this.#formContent) this.#formContent.hidden = false;
  }

  #showError(message) {
    const el = this.#errorEl;
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    this.#emailInput?.setAttribute('aria-invalid', 'true');
  }

  #hideError() {
    const el = this.#errorEl;
    if (!el) return;
    el.hidden = true;
    el.textContent = '';
  }

  async #handleSubmit() {
    const email = this.#emailInput?.value.trim() ?? '';
    const accountId = this.getAttribute('account-id') || '';

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.#showError(this.getAttribute('data-error-invalid-email') || 'Invalid email address');
      this.#emailInput?.focus();
      return;
    }

    if (!accountId) {
      this.#showError(this.getAttribute('data-error-configuration') || 'Configuration error');
      return;
    }

    this.#hideError();
    if (this.#submitBtn) this.#submitBtn.disabled = true;

    const dialog = this.#dialog;
    const variantId = dialog?.dataset.variantId ?? '';
    const listId = dialog?.dataset.listId ?? '';
    const newsletterChecked = this.querySelector('.klaviyo-bis-newsletter-checkbox')?.checked ?? false;
    const catalogVariantId = `$shopify:::$default:::${variantId}`;
    const revision = '2026-04-15';

    try {
      const res = await fetch(
        `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${accountId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                channels: ['EMAIL'],
                profile: { data: { type: 'profile', attributes: { email } } },
              },
              relationships: {
                variant: { data: { type: 'catalog-variant', id: catalogVariantId } },
              },
            },
          }),
        }
      );

      if (!res.ok && res.status !== 202) throw new Error(`${res.status}`);

      if (newsletterChecked && listId) {
        await fetch(
          `https://a.klaviyo.com/client/subscriptions/?company_id=${accountId}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', revision },
            body: JSON.stringify({
              data: {
                type: 'subscription',
                attributes: {
                  profile: { data: { type: 'profile', attributes: { email } } },
                },
                relationships: {
                  list: { data: { type: 'list', id: listId } },
                },
              },
            }),
          }
        ).catch(() => {});
      }

      if (this.#formContent) this.#formContent.hidden = true;
      if (this.#successEl) this.#successEl.hidden = false;
    } catch {
      this.#showError(this.getAttribute('data-error-request-failed') || 'Request failed. Please try again.');
    } finally {
      if (this.#submitBtn) this.#submitBtn.disabled = false;
    }
  }
}

if (!customElements.get('klaviyo-bis')) {
  customElements.define('klaviyo-bis', KlaviyoBis);
}
